import React, { useEffect, useState } from 'react';
import {
    Box, Chip, CircularProgress, Fab, Table, TableBody, TableCell,
    TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import DownloadingIcon from '@mui/icons-material/Downloading';
import { getCJWDiBatchCostFiles } from '../../API/cjwDiBatchCostFilesApi';
import { downloadErrorReport } from '../../API/costReportFileApi';
import { localDatabaseUrls } from '../../backend/constants';

const displayValue = (...values) => {
    const value = values.find((candidate) => candidate !== null && candidate !== undefined && candidate !== '');
    return value === undefined ? 'N/A' : value;
};

const getStatusColor = (status) => {
    const normalized = status?.toLowerCase() || '';
    if (normalized.includes('approved') || normalized.includes('processed')) return 'success';
    if (normalized.includes('draft') || normalized.includes('uploaded')) return 'warning';
    if (normalized.includes('excluded') || normalized.includes('rejected') || normalized.includes('error')) return 'error';
    if (normalized.includes('pending') || normalized.includes('progress')) return 'info';
    return 'default';
};

const Message = ({ children }) => (
    <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="body2" color="text.secondary">{children}</Typography>
    </Box>
);

const CJWLinkedCostFiles = ({ batchId }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(Boolean(batchId));
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        let active = true;

        if (!batchId) {
            setFiles([]);
            setLoading(false);
            setLoadFailed(false);
            return () => { active = false; };
        }

        setLoading(true);
        setLoadFailed(false);
        getCJWDiBatchCostFiles(batchId)
            .then((result) => {
                if (active) setFiles(Array.isArray(result) ? result : []);
            })
            .catch(() => {
                if (active) {
                    setFiles([]);
                    setLoadFailed(true);
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => { active = false; };
    }, [batchId]);

    if (loading) {
        return (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', display: 'flex', gap: 1, alignItems: 'center' }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading related cost files...</Typography>
            </Box>
        );
    }
    if (loadFailed) return <Message>Cost file availability could not be loaded.</Message>;
    if (files.length === 0) return <Message>No related cost files yet.</Message>;

    const handleDownload = async (file) => {
        if (!file?.id) return;
        try {
            await downloadErrorReport(file.id, file.fileName, localDatabaseUrls.serviceURL.originalFilePrefix);
        } catch (error) {
            console.error('Error downloading cost file:', error);
        }
    };

    return (
        <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderTop: '2px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#001489' }}>
                Related Cost Files ({files.length})
            </Typography>
            <Table size="small" sx={{ bgcolor: 'white' }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Uploaded Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>APG/ASG</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Rows (Valid/Total)</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Download cost file</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {files.map((file) => (
                        <TableRow key={file.id} hover>
                            <TableCell>{displayValue(file.fileName)}</TableCell>
                            <TableCell>{displayValue(file.uploadedBy)}</TableCell>
                            <TableCell>{displayValue(file.uploadedDate)}</TableCell>
                            <TableCell>{displayValue(file.category)}</TableCell>
                            <TableCell>{displayValue(file.costType, 'ASG')}</TableCell>
                            <TableCell>
                                <Chip label={displayValue(file.status)} size="small" color={getStatusColor(file.status)} sx={{ fontSize: '0.75rem' }} />
                            </TableCell>
                            <TableCell>
                                {file.validRows != null && file.totalRows != null
                                    ? `${file.validRows}/${file.totalRows}`
                                    : 'N/A'}
                            </TableCell>
                            <TableCell align="center">
                                <Tooltip title="Download cost file">
                                    <span>
                                        <Fab
                                            size="small"
                                            sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&:hover': { backgroundColor: 'transparent' } }}
                                            disabled={!file.id}
                                            aria-label={`Download ${file.fileName || 'cost file'}`}
                                            onClick={() => handleDownload(file)}
                                        >
                                            <DownloadingIcon sx={{ color: file.id ? 'black' : 'grey' }} />
                                        </Fab>
                                    </span>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

export default CJWLinkedCostFiles;
